import {computed, defineComponent, ref, watch} from 'vue';
import {OrganisationInvoiceSetting} from "@/api/klip-api.proxy";
import {useForm} from "vee-validate";
import {useKlModalEventsUtil} from "@/app/shared/helpers/kl-modal-events-util";
import * as yup from 'yup';
import {
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';
import {toTypedSchema} from '@vee-validate/yup';
import {Mri} from "@/app/mri/shared/services/services";
import {createAddressSchema} from "@/app/shared/components/kl-address-form/kl-address-form";

export default defineComponent({
    name: 'KlInvoiceSettingEdit',
    props: {
        invoiceSetting: {
            type: Object as () => OrganisationInvoiceSetting,
            default: () => ({}),
            required: true
        },
        showContactInfo: {
            type: Boolean,
            default: false,
            required: false
        },
        showAddressInfo: {
            type: Boolean,
            default: false,
            required: false
        }
    },
    emits: ['onSave'],
    setup(props, { emit }) {

        const schema = yup.object({
            vatNumber: yup.string().notRequired().label('BTW-nummer'),
            reminderEmail: yup.string().notRequired().email().max(200).label('E-mailadres voor factuurherinneringen'),
            attention: yup.string().notRequired().max(200).label('Ter attentie van'),
            reference: yup.string().notRequired().max(25).label('Eigen referentie'),
            invoiceContactName: yup.string().notRequired().max(200).label('Naam'),
            invoiceContactEmail: yup.string().notRequired().email().max(320).label('Contactpersoon e-mail'),
            invoiceContactPhoneNumber: yup.string().notRequired().kl_phone().max(200).label('Telefoon'),
            address: createAddressSchema(),
        });

        const isSending = ref<boolean>(false);

        const form = useForm({
            validationSchema: toTypedSchema(schema),
            initialValues: props.invoiceSetting,
        });
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema);

        const vatNumber = fields.vatNumber.getValue<string>();
        const reminderEmail = fields.reminderEmail.getValue<string>();
        const attention = fields.attention.getValue<string>();
        const reference = fields.reference.getValue<string>();
        const invoiceContactName = fields.invoiceContactName.getValue<string>();
        const invoiceContactEmail = fields.invoiceContactEmail.getValue<string>();
        const address = fields.address.getValue<Mri.IAddress>();
        const invoiceContactPhoneNumber = fields.invoiceContactPhoneNumber.getValue<string>();

        const disableSubmit = computed((): boolean => {
            return !form.meta.value.valid || !form.meta.value.dirty || isSending.value;
        });

        const invoiceSettingModalEvents = useKlModalEventsUtil('invoice-setting-modal', {
            onClosed() {
                form.resetForm();
                isSending.value = false;
            },
        });

        const organisationInvoiceSettingToSave = computed(() => ({
            ...props.invoiceSetting,
            reminderEmail: reminderEmail.value,
            attention: attention.value,
            reference: reference.value,
            address: address.value,
            invoiceContactName: invoiceContactName.value,
            invoiceContactEmail: invoiceContactEmail.value,
            invoiceContactPhoneNumber: invoiceContactPhoneNumber.value,
        }));

        const _saveOrganisationInvoiceSetting = async () => {
            isSending.value = true;
            emit('onSave', organisationInvoiceSettingToSave.value);
            invoiceSettingModalEvents.triggerModalToggle();
        }

        const _invoiceSettingChanged = (newInvoiceSetting: OrganisationInvoiceSetting) => {
            debugger;
            form.resetForm({
                values: newInvoiceSetting
            });

            invoiceSettingModalEvents.triggerModalToggle();
        };

        watch(() => props.invoiceSetting, (newValue) => _invoiceSettingChanged(newValue), { flush: 'sync' });

        const onSubmit = form.handleSubmit(_saveOrganisationInvoiceSetting);

        const cancel = () => {
            invoiceSettingModalEvents.triggerModalToggle();
        }

        return {
            isSending,
            disableSubmit,
            onSubmit,
            cancel,

            vatNumber,
            address,
            reminderEmail,
            attention,
            reference,
            invoiceContactName,
            invoiceContactEmail,
            invoiceContactPhoneNumber,

            fields,
        }
    }
})
